import React, { createContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, ConfigProvider } from 'antd';
import { FrontendAction } from '@owl-frontend/api-client/interface';
import { DialogLanding, useEffectWithPrev } from '@owl-frontend/components';

import { ErrorCode } from '@owl-lib/util';
import Spinner from '../../assets/app-spinner.svg';
import Routing from '../../components/Routing/Routing';
import { actions as sessionSliceActions } from '../../data/session/logic';
import type { StoreState } from '../../data/store';
import { actions as tenantSliceActions } from '../../data/tenant/logic';

/**
 * AppContainer
 * ============
 * Initial session and current User fetch contained here
 * - redirects to Cognito Login Flow when no valid session key established
 * - gets current User's Role from `sessionSliceActions.current` dispatch call
 */

export const Locale = React.createContext({
  messages: {},
  locale: 'en-ca',
});
export const LoadingContext = createContext<boolean>(false);
export const SessionContext = React.createContext<
  Omit<StoreState['session'], 'actions'>
>({
  features: {},
});

const AppContainer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const messages = useSelector((s: StoreState) => s['localization'].messages);
  const locale = useSelector((s: StoreState) => s['localization'].locale);
  const sessionActions = useSelector((s: StoreState) => s['session'].actions);
  const currentUser = useSelector((s: StoreState) => s['session'].current);

  const { features, current } = useSelector((s: StoreState) => s['session']);

  /**
   * MARK: EFFECTS
   */

  React.useEffect(() => {
    dispatch(sessionSliceActions.current());
    dispatch(sessionSliceActions.fetchFeatures());
    dispatch(tenantSliceActions.fetchTenant());
  }, []);

  React.useEffect(() => {
    if (params.get('utm_source')) {
      dispatch(
        sessionSliceActions.logEvent({
          events: [
            {
              action: FrontendAction.UtmSource,
              source: params.get('utm_source'),
            },
          ],
        })
      );

      navigate(location.pathname);
    }
  }, [params, location, navigate]);

  useEffectWithPrev(
    (prevAction) => {
      if (prevAction?.status !== 'pending') {
        return;
      }

      const { status, error } = sessionActions.current;

      if (status === 'fulfilled') {
        return;
      }

      if (error?.message === 'TIMED_OUT') {
        Modal.error({
          title: messages['shared.error'],
          content: messages['session.error.timeout'],
          onOk: () => {
            window.location.href = '/api/login';
          },
        });

        return;
      }

      if (error?.code === ErrorCode.ForbiddenGeneric) {
        window.location.href = '/api/login';
      }
    },
    // eslint-disable-next-line react-hooks2/exhaustive-deps
    [sessionActions.current, location.pathname, messages]
  );

  /**
   * MARK: RENDER
   */

  if (!currentUser || sessionActions.fetchFeatures.status === 'pending') {
    return (
      <div className="app-spinner">
        <Spinner />
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Progress: {
            defaultColor: '#7760d7',
          },
        },
        token: {
          colorPrimary: '#7760d7',
          colorPrimaryBg: '#714dc609',
          colorPrimaryBorder: '#714dc639',

          colorLink: '#7760d7',
          colorError: '#ff4665',
          colorErrorBg: '#ff466509',
          colorSuccess: '#2ed22b',
          colorSuccessBg: '#7cea6209',
          colorWarning: '#ffb390',
          colorWarningBg: '#e66f2d09',

          colorText: '#000000',
          colorTextSecondary: '#656560',
          colorTextTertiary: '#757570',

          colorBorder: '#d4d4df',
          colorBorderSecondary: '#e9e9ef',

          colorFill: '#0000000f',
          colorFillSecondary: '#efefef',
          boxShadowTertiary:
            '1px 1px 2px 0px rgba(0, 0, 0, 0.05),  -1px -1px 1px 0px rgba(0, 0, 0, 0.03)',
        },
      }}
    >
      <Locale.Provider
        value={{
          messages,
          locale,
        }}
      >
        <SessionContext.Provider
          value={{
            features,
            current,
          }}
        >
          <Routing />
          <DialogLanding />
        </SessionContext.Provider>
      </Locale.Provider>
    </ConfigProvider>
  );
};
export default AppContainer;
